import { useSignal, useSignalEffect } from "@preact/signals-react";
import { setupHandlers, setupLocalState } from "../../library/dataService";
import { log } from "../../library/logger";
import { INGHtmlProps } from "../../library/NGFieldExtensions";
import { getClassName, getTestId, getsxObject } from "../../library/utils";
import { Box } from "@mui/material";
import { validateCookiesForUrl } from "../../library/cookieValidation";

const tag = "NGHtml";

export default function NGHtml({ config, context }: INGHtmlProps) {
  const local = setupLocalState(
    config,
    {
      Value: useSignal(config.Value ?? ""),
      Visible: useSignal(config.Visible ?? true),
      Classes: useSignal(config.Classes ?? ""),
      Style: useSignal(config.Style ?? {}),
    },
    context
  );

  const handlers = setupHandlers(config, context);

  const sanitizedHtml = useSignal("");

  // Validate cookies for any internal URLs found in the HTML content
  const cookieValidated = useSignal(false);

  useSignalEffect(() => {
    const validateHtmlResources = async () => {
      if (!local.Value.value) {
        cookieValidated.value = true;
        return;
      }

      try {
        // Extract URLs from the HTML content (src attributes, href attributes, etc.)
        const urlRegex = /(src|href)=["']([^"']+)["']/gi;
        const urls: string[] = [];
        let match;
        while ((match = urlRegex.exec(local.Value.value)) !== null) {
          urls.push(match[2]);
        }

        // Validate cookies for each internal URL found
        for (const url of urls) {
          await validateCookiesForUrl(url);
        }

        cookieValidated.value = true;
      } catch (error) {
        log.error(tag, "Cookie validation failed for HTML resources:", error);
        cookieValidated.value = false;
      }
    };

    validateHtmlResources();
  });

  useSignalEffect(() => {
    if (!cookieValidated.value) return;

    const html = local.Value.value;
    (async () => {
      const purify = await import("dompurify");
      sanitizedHtml.value = purify.default().sanitize(html, {
        ADD_TAGS: ["iframe"],
        ADD_ATTR: ["allow", "allowfullscreen", "frameborder", "scrolling"],
      });
      log.info(tag, "sanitized html", html, sanitizedHtml);
    })();
  });

  return (
    <>
      {local.Visible.value && local.Value.value && cookieValidated.value && (
        <Box
          data-testid={getTestId(config)}
          data-type={config.__typename}
          sx={getsxObject(local.Style.value)}
          className={getClassName(local.Classes)}
          dangerouslySetInnerHTML={{ __html: sanitizedHtml.value }}
          {...handlers}
        ></Box>
      )}
    </>
  );
}
